/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * A compact CSS-grid table for embedded sub-lists.
 *
 * Generalises the TaxesTable pattern. Deliberately not DataTable: that brings
 * a search box, an entries-per-page selector and pagination, which over three
 * rows inside a card is absurd and visually collides with the surrounding
 * layout.
 */

import React from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

export default function MiniTable({ columns, rows, emptyMessage, onRowClick, maxRows }) {
  const template = columns.map((c) => c.width || "1fr").join(" ");
  const visible = maxRows ? rows.slice(0, maxRows) : rows;

  if (!rows.length) {
    return (
      <MDTypography variant="button" color="text">
        {emptyMessage}
      </MDTypography>
    );
  }

  return (
    <MDBox sx={{ border: "1px solid #E8EDF5", borderRadius: "8px", overflow: "hidden" }}>
      <MDBox
        sx={{
          display: "grid",
          gridTemplateColumns: template,
          bgcolor: "#F4F6F9",
          px: 1.5,
          py: 1,
          gap: 1,
        }}
      >
        {columns.map((col) => (
          <MDTypography
            key={col.key}
            variant="caption"
            fontWeight="bold"
            color="text"
            sx={{ letterSpacing: "0.03em", textTransform: "uppercase" }}
          >
            {col.label}
          </MDTypography>
        ))}
      </MDBox>

      {visible.map((row, index) => (
        <MDBox
          key={row.id ?? index}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          sx={{
            display: "grid",
            gridTemplateColumns: template,
            px: 1.5,
            py: 1,
            gap: 1,
            alignItems: "center",
            borderBottom: index === visible.length - 1 ? "none" : "1px solid #E8EDF5",
            cursor: onRowClick ? "pointer" : "default",
            "&:hover": onRowClick ? { bgcolor: "#FAFBFD" } : undefined,
          }}
        >
          {columns.map((col) => (
            <MDBox key={col.key} sx={{ minWidth: 0 }}>
              {col.render ? col.render(row) : (
                <MDTypography variant="button" color="dark" sx={{ wordBreak: "break-word" }}>
                  {row[col.key] ?? "—"}
                </MDTypography>
              )}
            </MDBox>
          ))}
        </MDBox>
      ))}

      {maxRows && rows.length > maxRows && (
        <MDBox px={1.5} py={1} sx={{ borderTop: "1px solid #E8EDF5" }}>
          <MDTypography variant="caption" color="text">
            {`Showing ${maxRows} of ${rows.length}`}
          </MDTypography>
        </MDBox>
      )}
    </MDBox>
  );
}

MiniTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      width: PropTypes.string,
      render: PropTypes.func,
    })
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  emptyMessage: PropTypes.string,
  onRowClick: PropTypes.func,
  maxRows: PropTypes.number,
};

MiniTable.defaultProps = {
  rows: [],
  emptyMessage: "Nothing to show.",
  onRowClick: null,
  maxRows: null,
};
